import { useState, useEffect, useMemo } from "react";
import {
  Box,
  Modal,
  ModalBody,
  Button,
  Text,
  Select,
  FlexiPagination,
  Input,
  EmptyState,
  Notify,
  Grid,
  Checkbox,
  Spacer,
  TextArea,
} from "@flexisaf/flexibull2";
import styled from "styled-components";
import Theme from "../../../theme";
import { questionTypes } from "../../../utils/constants";
import useSchoolsGet from "../../../hooks/questions/useSchoolsGet";
import usePapersGet from "../../../hooks/papers/usePapersGet";
import useAIQuestionCreate from "../../../hooks/questions/useAIQuestionCreate";
import usePaginationWrapper from "../../../hooks/general/usePaginationWrapper";
// import CircleProgressBar from "../../custom/CircleProgressBar";
import ContentRenderer from "../ContentRenderer";

const GptEditor = ({ open, currentPaper, closeModal, refetch }) => {
  const [selectedPaper, setSelectedPaper] = useState();
  const { papers } = usePapersGet({ id: "" });
  const { schools } = useSchoolsGet();
  const [questionCount, setQuestionCount] = useState(20);
  const [optionCount, setOptionCount] = useState(4);
  const [questionList, setQuestionList] = useState([]);
  const [paginatedList, setPaginatedList] = useState([]);
  const [selectedQuestionType, setSelectedQuestionType] = useState(
    questionTypes[0]
  );
  const [saveAndContinue, setSaveAndContinue] = useState(false);
  const [contentFile, setContentFile] = useState(null);

  const [tagQuestion, setTagQuestion] = useState(false);
  const [department, setDepartment] = useState("");
  const [institution, setInstitution] = useState(null);
  const [prompt, setPrompt] = useState("");
  // const [percent, setPercent] = useState(0);
  const [faculty, setFaculty] = useState("");
  const {
    limit,
    offset,
    setOffset,
    setLimit,
    totalElements,
    setPageable,
    page,
    setPage,
    pageOptions,
  } = usePaginationWrapper({ defaultLimit: 10 });

  const {
    isGenerating,
    isSaving,
    generatedQuestions,
    generateAIQuestions,
    resetAIQuestionReducer,
    saveAIQuestions,
  } = useAIQuestionCreate();

  const modGeneratedQuestions = useMemo(
    () =>
      generatedQuestions.map((question, index) => {
        return {
          ...question,
          id: `question-${index + 1}`,
          showSolution: false,
        };
      }),
    [generatedQuestions]
  );

  const onClose = () => {
    resetAIQuestionReducer();
    closeModal();
    document.body.style.overflow = "auto";
  };

  const handleQuestionsLimit = (value) => {
    let inputValue = parseInt(value, 10);

    if (inputValue < 1) {
      setQuestionCount(1);
    }

    if (inputValue <= 20) {
      setQuestionCount(inputValue);
    }
  };
  const handleFileUpload = (e) => {
    e.stopPropagation();

    const file = e?.target?.files[0];
    const uploadLimit = file?.size / 1024 / 1024 < 2.5;

    if (!uploadLimit) {
      Notify("File must not exceed 2.5MB", { status: "error" });
    } else if (
      file.type !== "application/pdf" &&
      !file.type.startsWith("image/")
    ) {
      Notify("Invalid file, please select a PDF or image file", {
        status: "error",
      });
      setContentFile(null);
    } else {
      setContentFile(file);
      // handleAddKnowledge(file);
    }
  };

  const increaseOptions = () => {
    setOptionCount((prevCount) => prevCount + 1);
  };

  const decreaseOptions = () => {
    setOptionCount((prevCount) => prevCount - 1);
  };

  const toggleSolution = (id) => {
    setQuestionList((prevList) => {
      return prevList.map((question) => {
        if (question.id === id) {
          return {
            ...question,
            showSolution: !question.showSolution,
          };
        }
        return question;
      });
    });
  };

  const deleteQuestion = (id) => {
    setQuestionList((prevList) => {
      return prevList.filter((question) => question.id !== id);
    });
  };

  const handleGenerateQuestions = () => {
    const formData = new FormData();
    formData.append("contentFile", contentFile);
    const payload = {
      data: {
        query: prompt,
        questionsCount: questionCount,
        questionType: selectedQuestionType.value,
        optionsCount: optionCount,
        paperId: selectedPaper.value,
        formData,
      },
    };
    generateAIQuestions(payload);
  };

  // const handleAddKnowledge = (file) => {
  //   const formData = new FormData();
  //   formData.append("file", file);
  //   const payload = {
  //     data: {
  //       paperName: selectedPaper.label,
  //       paperId: selectedPaper.value,
  //       formData,
  //     },
  //     onFailure: () => {
  //       setAddKnowledge(null);
  //     },
  //   };

  //   let progress = 0;
  //   const progressInterval = setInterval(() => {
  //     if (progress < 95) {
  //       progress += progress < 70 ? 5 : 2;
  //       setPercent(progress);
  //     } else {
  //       clearInterval(progressInterval);
  //       setPercent(0);
  //     }
  //   }, 100);

  //   saveAddingKnowledge(payload);
  // };

  const handleSubmit = () => {
    const questions = questionList.map(({ tags, ...rest }) => ({
      ...rest,
      tags: institution
        ? [institution.label, department, faculty].filter((tag) => tag !== "")
        : [],
    }));

    const payload = {
      data: {
        paperId: selectedPaper.value,
        questions,
      },
      callback: () => {
        if (saveAndContinue) {
          resetAIQuestionReducer();
        } else {
          onClose();
          refetch();
        }
      },
    };
    saveAIQuestions(payload);
  };

  useEffect(() => {
    setQuestionList(modGeneratedQuestions);
  }, [modGeneratedQuestions]);

  useEffect(() => {
    if (questionList) {
      setPageable({
        total_elements: questionList?.length,
        total_pages: Math.ceil(totalElements / limit),
        number_of_elements: limit,
      });
      setPaginatedList(questionList?.slice(offset, offset + limit));
    }
  }, [questionList, limit, offset, setPageable, totalElements]);

  useEffect(() => {
    if (currentPaper) {
      setSelectedPaper({
        label: currentPaper?.name || "",
        value: currentPaper?.id || "",
      });
    }
  }, [currentPaper]);

  useEffect(() => {
    if (!tagQuestion) {
      setInstitution(null);
    }
  }, [tagQuestion, institution]);

  return (
    <Modal open={open}>
      <ModalBody width="95%" style={{ height: "100%", maxWidth: "1400px" }}>
        <Box display="flex" height="100%">
          <Box
            width="25%"
            background={Theme.PrimarySurface}
            style={{
              borderRight: `1px solid ${Theme.PrimaryBorderColor}`,
              position: "relative",
              overflow: "scroll",
            }}
            pad="34px"
          >
            <Text bold block size="16px">
              Generate Question(s)
            </Text>

            <Box margin="15px 0" display="block">
              <Select
                placeholder="Select Paper"
                label="Paper"
                spaceTop="40px"
                spaceBottom="30px"
                required
                value={selectedPaper}
                onChange={(e) => setSelectedPaper(e)}
                options={
                  currentPaper?.id === undefined
                    ? []
                    : papers?.items?.map((subject) => ({
                        label: subject.name,
                        value: subject.id,
                      }))
                }
                style={{ width: "100%" }}
                disable
              />
              <Select
                label="Select Question Type"
                placeholder="E.g Multiple Choice"
                block
                spaceBottom="30px"
                value={selectedQuestionType}
                onChange={(value) => {
                  setSelectedQuestionType(value);
                }}
                options={questionTypes}
                defaultValue={questionTypes[0]}
              />

              <Input
                type="number"
                placeholder="Number of questions to generate"
                label="Number of questions"
                min={1}
                max={20}
                spaceTop="30px"
                spaceBottom="30px"
                block
                required
                value={questionCount}
                onChange={({ target: { value } }) =>
                  handleQuestionsLimit(value)
                }
              />
              <Box margin="-20px 0 0">
                <Box margin="5px 0">
                  <Text bold size="0.9em">
                    Number of options
                  </Text>
                </Box>
                <Box
                  display="flex"
                  style={{ alignItems: "center", gap: "10px" }}
                >
                  <Button
                    round
                    pale
                    onClick={decreaseOptions}
                    disabled={optionCount < 5}
                  >
                    <i className="saf-minus" />
                  </Button>
                  <Text bold> {optionCount} </Text>
                  <Button
                    round
                    pale
                    onClick={increaseOptions}
                    disabled={optionCount > 9}
                  >
                    <i className="saf-add" />
                  </Button>
                </Box>
              </Box>
              <Spacer space={20} />
            </Box>
            <PromptContainer>
              <TextArea
                placeholder="Type your prompt here"
                name="prompt"
                block
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
              />
              <div>
                <label htmlFor="contentFile" style={{ cursor: "pointer" }}>
                  <input
                    type="file"
                    id="contentFile"
                    onChange={(e) => handleFileUpload(e)}
                  />
                  <Box>
                    <i className="saf-document-upload" />
                  </Box>
                </label>
              </div>
              {contentFile && (
                <>
                  <UploadFileContainer>
                    <ProgressContainer>
                      {/* {isAddingKnowledge ? (
                        <Progress>
                          <CircleProgressBar progress={percent} />
                        </Progress>
                      ) : (
                        <i className="saf-document-cloud" />
                      )} */}
                      <i className="saf-document-cloud" />
                      <i
                        onClick={() => setContentFile(null)}
                        className="saf-close-circle"
                      />
                    </ProgressContainer>

                    <div className="file-info">
                      <i className="saf-file" />
                      <span className="file-name">{contentFile.name}</span>
                      <span className="file-size">
                        {(contentFile.size / 1024 / 1024).toFixed(2)}MB
                      </span>
                    </div>
                  </UploadFileContainer>
                  {/* <ProgressContainer>
                    <Background />
                    <Progress percent={percent} />
                  </ProgressContainer> */}
                </>
              )}
            </PromptContainer>

            <Box>
              <Checkbox
                label="Tag question(s)"
                checked={tagQuestion}
                onChange={(e) => setTagQuestion(e.target.checked)}
              />
              <Spacer space={30} />
              {tagQuestion && (
                <Box>
                  <Select
                    placeholder="Select Institution"
                    label="Associated School"
                    required
                    value={institution}
                    onChange={(value) => {
                      setInstitution(value);
                    }}
                    options={schools?.items?.map((item) => {
                      return {
                        label: item.name,
                        value: item.id,
                      };
                    })}
                    style={{ width: "100%" }}
                  />
                  <Spacer space={5} />
                  <Text color="#707070" size="0.8rem">
                    Select the school that owns the content being uploaded.
                  </Text>
                  <Input
                    type="text"
                    placeholder="Enter Faculty"
                    label="Faculty"
                    spaceTop="30px"
                    spaceBottom="30px"
                    block
                    value={faculty}
                    onChange={({ target: { value } }) => setFaculty(value)}
                  />
                  <Input
                    type="text"
                    placeholder="Enter Deparment"
                    label="Department"
                    spaceTop="30px"
                    spaceBottom="30px"
                    block
                    value={department}
                    onChange={({ target: { value } }) => setDepartment(value)}
                  />
                </Box>
              )}
            </Box>

            <Box
              display="flex"
              width="100%"
              style={{
                justifyContent: "end",
                gap: "20px",
              }}
            >
              <Button
                pale
                color={Theme.PrimaryGrey}
                fontColor={Theme.PrimaryGrey}
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                progress={isGenerating}
                onClick={handleGenerateQuestions}
                disabled={!prompt || isSaving || (tagQuestion && !institution)}
              >
                {"Generate"}
              </Button>
            </Box>
          </Box>

          <Box width="75%" height="100%" style={{ overflow: "scroll" }}>
            <Box pad=" 30px 20px" style={{ borderBottom: "1px solid #CCD5E4" }}>
              <Grid default="auto max-content" alignItems="center">
                <Text size="20px" bold>
                  {currentPaper?.name}
                </Text>
                <Box display="flex" style={{ gap: "20px" }}>
                  <Checkbox
                    label="Save and continue"
                    checked={saveAndContinue}
                    onChange={(e) => setSaveAndContinue(e.target.checked)}
                  />

                  <Button
                    iconLeft={
                      <i
                        className="saf-document-download"
                        style={{ fontSize: "16px" }}
                      />
                    }
                    disabled={!questionList.length}
                    progress={isSaving}
                    onClick={handleSubmit}
                  >
                    Save
                  </Button>
                  <Button
                    pale
                    color={Theme.PrimaryRed}
                    onClick={resetAIQuestionReducer}
                    disabled={!questionList.length}
                  >
                    <Text size="18px" color={Theme.PrimaryRed}>
                      <i className="saf-trash" />
                    </Text>
                  </Button>
                </Box>
              </Grid>
            </Box>
            <Box>
              {questionList.length ? (
                <Box pad="40px 20px">
                  <Grid default="repeat(2, 390px)" columnGap="100px">
                    {paginatedList &&
                      paginatedList.map((question, index) => {
                        const correctAnswer = question?.answerOptions
                          ?.map((option, answerIndex) => ({
                            ...option,
                            index: answerIndex,
                          }))
                          ?.filter((option) => option.correct !== false);

                        return (
                          <QuestionWrapper key={question.id}>
                            <Grid default="20px 1fr 50px">
                              <Text size="16px" bold>
                                {index + 1}.
                              </Text>
                              <Text size="16px" bold>
                                <ContentRenderer content={question.text} />
                              </Text>

                              <Button
                                color={Theme.PrimaryRed}
                                font="14px"
                                pad="0 10px"
                                height={28}
                                pale
                                onClick={() => deleteQuestion(question.id)}
                              >
                                <Text color={Theme.PrimaryRed}>
                                  <i className="saf-trash" />
                                </Text>
                              </Button>
                            </Grid>
                            <Box pad="10px 0 0 40px">
                              <Text>Topic: {question.topic}</Text>
                            </Box>
                            <ul>
                              {!question.showSolution &&
                                question.answerOptions?.map(
                                  (options, index) => {
                                    return (
                                      <AnswerOptions
                                        key={`${question.id}-${index}`}
                                        index={index}
                                        text={options.text}
                                      />
                                    );
                                  }
                                )}

                              {question.showSolution && (
                                <Box>
                                  <ContentRenderer
                                    content={question.solution}
                                  />
                                </Box>
                              )}
                            </ul>
                            <Box
                              display="flex"
                              style={{ justifyContent: "space-between" }}
                            >
                              <Box display="flex" pad="0 0 0 40px">
                                {selectedQuestionType.value !== "LONG_TEXT" && (
                                  <Text bold>Answer:</Text>
                                )}
                                <ul
                                  style={{
                                    padding: "0 10px",
                                    marginTop: "1px",
                                  }}
                                >
                                  {correctAnswer?.map((options) => {
                                    return (
                                      <AnswerOptions
                                        key={options.index}
                                        index={options.index}
                                        text={options.text}
                                      />
                                    );
                                  })}
                                  {question?.answerTexts?.map(
                                    (options, index) => {
                                      return (
                                        <AnswerOptions
                                          key={index}
                                          index={index}
                                          text={options}
                                          hide
                                        />
                                      );
                                    }
                                  )}
                                </ul>
                              </Box>

                              <Text
                                color={Theme.PrimaryColor}
                                style={{
                                  alignSelf: "flex-start",
                                  borderBottom: `1px solid ${Theme.PrimaryColor}`,
                                  cursor: "pointer",
                                }}
                                onClick={() => toggleSolution(question.id)}
                              >
                                {question.showSolution ? "Hide" : "Show"}{" "}
                                explanation
                              </Text>
                            </Box>
                          </QuestionWrapper>
                        );
                      })}
                  </Grid>
                  <FlexiPagination
                    pageCounts={pageOptions}
                    itemsDisplayed
                    total={totalElements}
                    pageSize={limit}
                    onChange={(page) => {
                      setPage(page);
                      setOffset(page - 1);
                    }}
                    changePageSize={({ value }) => setLimit(value)}
                    current={page}
                  />
                </Box>
              ) : (
                <EmptyState
                  type="customers"
                  title="Nothing to see yet!"
                  info="Your questions will appear here after they are generated."
                />
              )}
            </Box>
          </Box>
        </Box>
      </ModalBody>
    </Modal>
  );
};

export default GptEditor;

const AnswerOptions = ({ text, index, hide = false }) => {
  const label = String.fromCharCode(65 + index);

  return (
    <OptionItem>
      {label}
      {hide ? "" : ")"}
      <ContentRenderer content={text} />
    </OptionItem>
  );
};

const QuestionWrapper = styled.div`
  & ul {
    list-style: none;
    top: -29px;
    right: -141px;
    color: #72777a;
    max-height: 80px;
    overflow: scroll;
  }
`;

const OptionItem = styled.li`
  display: inline-flex;
  align-items: center;
  margin-right: 15px;
`;

export const PromptContainer = styled.div`
  width: 100%;

  & button {
    all: unset;
  }
  label {
    position: relative;
    top: -29px;
    right: -240px;
  }
  & i {
    font-size: 1.2rem;
  }
  & .flexi-text-area {
    min-height: 80px;
  }

  & input {
    width: 0.1px;
    height: 0.1px;
    opacity: 0;
    overflow: hidden;
    position: absolute;
    z-index: -1;
  }
`;
const UploadFileContainer = styled.div`
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 12px;
  margin-top: -1rem;

  .file-info {
    display: flex;
    align-items: center;
    gap: 10px;

    .file-name {
      flex: 1;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .file-size {
      color: #666;
      font-size: 12px;
    }
  }

  .close-btn {
    position: absolute;
    top: 8px;
    right: 8px;
    cursor: pointer;
    color: #666;
  }
`;

const ProgressContainer = styled.div`
  padding: 0 1rem 0.5rem 1rem;
  display: flex;
  justify-content: space-between;
`;

// const BaseBox = styled.div`
//   height: 100%;
//   position: absolute;
//   left: 0;
//   top: 0;
//   border-radius: 3px;
//   transition: width 10s ease-in-out;
// `;

// const Progress = styled(BaseBox)`
//   position: relative;
//   width: 25px;
//   height: 25px;
// `;
